import gql from 'graphql-tag';
import { PLANOGRAM_ITEM_PLANOGRAM_DETAIL_FRAGMENT } from '../planogramItem';

const MACHINE_SETUP_FRAGMENT = gql`
	fragment machineSetup on MachineSetup {
		id
		identifier
		machine {
			id
			assetId
			type
			destroyed
			location {
				id
				name
				destroyed
				isWarehouse
			}
			previousLocation {
				id
				name
				isWarehouse
			}
			place {
				id
				name
				location {
					id
					isWarehouse
				}
			}
		}
		plannedMachine {
			id
			assetId
			type
			destroyed
			location {
				id
				name
				destroyed
				isWarehouse
			}
			place {
				id
				name
			}
		}
		movePlanId
		moveSourceLocation {
			id
			name
			isWarehouse
		}
		moveSourcePlace {
			id
			name
		}
		movePlannedAt
		movePlannedBy {
			id
			firstname
			lastname
		}
		moveConfirmedAt
		moveConfirmedBy {
			id
			firstname
			lastname
		}
		commDevice {
			id
			deviceId
			secondaryId
			setupInformation
			lastCommunication
		}
		locationSetup {
			id
			dueDate
			identifier
			setupTeamUpdate
			location {
				id
				name
				destroyed
			}
		}
		route {
			id
			name
		}
		place {
			id
			name
			destroyed
		}
		planogram {
			id
			name
		}
		assignees {
			id
			firstname
			lastname
			username
		}
		tags {
			id
			type
			name
		}
		commDevice {
			id
			deviceId
		}
		finalPhotos {
			id
			link
			comment
			created
		}
		placePhotos {
			id
			link
			comment
			created
		}
		machineQualityObject
		machineSizeObject
		requiredDoorAngle
		requiresCreditCardReader
		requiresCashPayments
		healthCompliant
		requiresExtensionLead
		additionalInstructions
		status
		created
		creator {
			id
			firstname
			lastname
			username
		}

		checkPowerCord
		cleanMachine
		install8888LockBarrel
		turnOffGlassHeater
		unlockCoinBox
		installPaddingInDropBin
		ensureMachineIdIsPresentOnMachine
		applyPricingStickerOrPriceTags
		installNoteReader
		ensureNoteReaderAcceptsNewBills

		installCoinMech
		fillCoinMechFloatInstallCassette
		testCoinMechByDispensingCoins
		installCreditCardReader
		alignAllCoilsAndTest
		alignSelectionNumbers
		priceMachineElectronically
		ensureProductsFitCoils
		setMachineTemperature
		removeTipperBars
		testAllPaymentDevices
		sendDexFile
		cleanMachineCompressor
		leaveVentilationChute
		updatePosSignage
		loadBranding
		imageUrl
		machineType {
			id
			name
		}
		machineKey {
			id
			name
		}
		machineWrap {
			id
			name
		}
		machineModel {
			id
			name
		}
		machineDistributor {
			id
			name
		}
		machineCoinMech {
			id
			name
		}
		machineNoteReader {
			id
			name
		}		
		machineBrand
		machineSerialNumber
		
		buildDate

		creditCardReaderType
		paymentDeviceSerialNo
		verifyCreditCardTransaction
		sendEmail
		removeOldDirectionsAndNotes
		updateAllMachineInformation
	}
`;

const MACHINE_SETUP_PLANOGRAM_FRAGMENT = gql`
	fragment machineSetupPlanogram on Planogram {
		id
		name
		items {
			...planogramItemPlanogramDetail
		}
		ingredientPlanogram {
			id
			template
			isIngredient
			items {
				...planogramItemPlanogramDetail
			}
		}
		planogramItemGroups {
			id
			name
			type
			order
			layoutSettings
			serialNumber
			goveeDevice {
				id
				name
				settings
			}
			model {
				id
				name
			}
			lastTemperatureReading
		}
	}
	${PLANOGRAM_ITEM_PLANOGRAM_DETAIL_FRAGMENT}
`;

const MACHINE_SETUP_DETAIL_FRAGMENT = gql`
	fragment machineSetupDetail on MachineSetup {
		...machineSetup
		planogram {
			...machineSetupPlanogram
		}
	}
	${MACHINE_SETUP_FRAGMENT}
	${MACHINE_SETUP_PLANOGRAM_FRAGMENT}
`;

export const CREATE_MACHINE_SETUP = gql`
    mutation createMachineSetupMutation($input: CreateMachineSetupInput!) {
        createMachineSetup(input: $input) {
            ...machineSetup
        }
	}
	${MACHINE_SETUP_FRAGMENT}
`;

export const GET_MACHINE_SETUPS = gql`
	query(
		$page: Int!,
		$perPage: Int!,
		$sortBy: String!,
		$sortDirection: Int!,
		$filter: MachineSetupPageFilter
	) {
		getMachineSetups(
			page: $page,
			perPage: $perPage,
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				...machineSetup
			}
			count
		}
	}
	${MACHINE_SETUP_FRAGMENT}
`;

export const GET_MACHINE_SETUPS_COUNT = gql`
	query {
		countMachineSetups
	}
`;

export const DESTROY_MACHINE_SETUP = gql`
	mutation destroyMachineSetupMutation ($id: ID!) {
		destroyMachineSetup(id: $id)
	}
`;


export const GET_MACHINE_SETUP = gql`
	query($id: String) {
		getMachineSetup(id: $id) {
			...machineSetupDetail
		}
	}
	${MACHINE_SETUP_DETAIL_FRAGMENT}
`;

export const MODIFY_MACHINE_SETUP = gql`
	mutation modifyMachineSetupMutation($input: ModifyMachineSetupInput!) {
		modifyMachineSetup(input: $input) {
			machinesetup {
				...machineSetupDetail
			}
		}
	}
	${MACHINE_SETUP_DETAIL_FRAGMENT}
`;

export const PLAN_MACHINE_SETUP_MOVE = gql`
	mutation planMachineSetupMoveMutation($setupId: ID!, $machineId: ID!) {
		planMachineSetupMove(setupId: $setupId, machineId: $machineId) {
			...machineSetup
		}
	}
	${MACHINE_SETUP_FRAGMENT}
`;

export const CONFIRM_MACHINE_SETUP_MOVE = gql`
	mutation confirmMachineSetupMoveMutation($setupId: ID!, $movePlanId: ID!) {
		confirmMachineSetupMove(setupId: $setupId, movePlanId: $movePlanId) {
			...machineSetupDetail
		}
	}
	${MACHINE_SETUP_DETAIL_FRAGMENT}
`;

export const CANCEL_MACHINE_SETUP_MOVE = gql`
	mutation cancelMachineSetupMoveMutation($setupId: ID!, $movePlanId: ID!) {
		cancelMachineSetupMove(setupId: $setupId, movePlanId: $movePlanId) {
			...machineSetupDetail
		}
	}
	${MACHINE_SETUP_DETAIL_FRAGMENT}
`;
